import { useKeycloak } from "@react-keycloak/web";
import { router } from "./Router";
import { useCallback, useEffect, useState } from "react";
import { storageAuthTokenSave } from "./storage/storageAuthToken";
import { storageUserSave } from "./storage/storageUser";

import "./global.scss";
import { TUserInfo } from "./@types/user-info.type";
import { configureApiResponseInterceptor } from "./services/api/ApiConfig";
import { ensureUser } from "./services/api/user/UserService";
import { IUserInformation } from "./@types/user-information.type";
import { getAllTenantsByAccount } from "./services/api/tenant/TenantService";
import { RouterProvider } from "react-router-dom";
import {
  storageSelectedTenantGet,
  storageSelectedTenantSave,
} from "./storage/storageSelectedTenant";
import { ITenant } from "./@types/tenant.type";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

const TOKEN_REFRESH_INTERVAL = 60000; // 1 min
const TOKEN_EXPIRY_BUFFER = 30; // 30 seg

export function App() {
  const { keycloak, initialized } = useKeycloak();
  const [userInfo, setUserInfo] = useState<TUserInfo | undefined>();
  const [tenants, setTenants] = useState<ITenant[] | undefined>();

  const handleTokenUpdate = useCallback(() => {
    if (keycloak.authenticated) {
      keycloak.updateToken(TOKEN_EXPIRY_BUFFER).catch(() => {
        keycloak.login();
      });
    }
  }, [keycloak]);

  const fetchUserInfo = useCallback(async () => {
    if (initialized && keycloak.authenticated) {
      try {
        storageAuthTokenSave(keycloak.token ?? "");
        const user = (await keycloak.loadUserInfo()) as TUserInfo;
        const userTenants = await getAllTenantsByAccount(user.email);
        if (!userTenants || userTenants.length == 0) {
          setTenants([]);
          router.navigate("/new-tenant");
        } else {
          setTenants(userTenants);
          user.tenants = tenants;
          const selectedTenant = storageSelectedTenantGet();
          storageSelectedTenantSave(
            userTenants?.find((x) => x.tenantName == selectedTenant)
              ?.tenantName ?? userTenants[0].tenantName
          );
          let userInformation: IUserInformation = {
            firstname: user.given_name,
            lastname: user.family_name,
            email: user.email,
          };
          userInformation = await ensureUser(userInformation);
          user.id = userInformation.id as string;
          user.resourceId = userInformation.resourceId;
          user.features = userInformation.features;
          storageUserSave(user);
          setUserInfo(user);
          configureApiResponseInterceptor(keycloak);
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, [initialized, keycloak]);

  useEffect(() => {
    if (initialized) {
      if (!keycloak.authenticated) {
        keycloak.login();
      } else {
        fetchUserInfo();
      }
    }
  }, [initialized, keycloak, fetchUserInfo]);

  useEffect(() => {
    const interval = setInterval(handleTokenUpdate, TOKEN_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [handleTokenUpdate]);

  const handleLogout = useCallback(() => {
    keycloak.logout().catch((error) => console.error(error));
  }, [keycloak]);

  return <>{tenants && <RouterProvider router={router} />}</>;
}
